import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Copy, Globe, Phone, Smartphone, Camera, UploadCloud, Loader, Clock, XCircle } from 'lucide-react';
import { fileToUpload } from '../utils/docUpload';

// A notice the DRIVER pays themselves — a toll payment demand. Tolls can't be nominated
// to the driver, so no second notice comes in their name: the one on their shelf is the
// one to pay. The panel says how to pay it, step by step, then takes the receipt (PDF,
// screenshot or a photo); fine-receipt-sb reads the receipt number and stamps it paid.
// Server shape: doc.pay from _shared/fine-pay.js payView.

const API = '/.netlify/functions/fine-receipt-sb';
const fmtMoney = (n) => (n == null ? '' : Number(n).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }));
const fmtDay = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
const telHref = (p) => 'tel:' + (p || '').replace(/[^\d+]/g, '');

const TONE = {
  ok:      { bg: 'rgba(245,158,11,0.10)', bd: '#d97706', fg: '#b45309' },
  soon:    { bg: 'rgba(234,88,12,0.12)',  bd: '#ea580c', fg: '#c2410c' },
  due:     { bg: 'rgba(214,69,69,0.12)',  bd: '#dc2626', fg: '#dc2626' },
  overdue: { bg: 'rgba(214,69,69,0.16)',  bd: '#dc2626', fg: '#dc2626' },
  paid:    { bg: 'rgba(46,125,50,0.10)',  bd: '#16a34a', fg: '#16a34a' },
};

function countdown(pay) {
  const d = pay.daysLeft;
  if (d == null) return '';
  if (d < 0) return `OVERDUE by ${-d} day${d === -1 ? '' : 's'}`;
  if (d === 0) return 'Due TODAY';
  return `${d} day${d === 1 ? '' : 's'} left`;
}

function CopyRow({ label, value }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch (_) { /* old browser — the number is on screen */ }
  };
  return (
    // Label sits above the number so a long BPAY reference (17 digits) stays on one line
    // at phone width — a number split across lines is easy to mistype into a bank app.
    <div style={{ fontSize: '0.84rem' }}>
      <div style={{ color: 'var(--hp-dim)', fontSize: '0.74rem', marginBottom: 2 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <b style={{ fontFamily: 'var(--font-mono)', fontSize: '0.92rem', whiteSpace: 'nowrap', overflowX: 'auto', minWidth: 0 }}>{value}</b>
      <button onClick={copy} aria-label={`Copy ${label}`}
        style={{ marginLeft: 'auto', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: '0.72rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 6, background: 'var(--hp-surface)', color: done ? 'var(--hp-green)' : 'var(--hp-text)', cursor: 'pointer' }}>
        {done ? <><CheckCircle size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
      </button>
      </div>
    </div>
  );
}

export default function FinePayPanel({ doc, onChanged }) {
  const pay = doc.pay;
  const fine = doc.fine || {};
  const h = pay.howToPay || {};
  const p = pay.payment;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const paid = p && p.status === 'paid';
  const tone = TONE[paid ? 'paid' : pay.level] || TONE.ok;
  const issuer = h.issuer || 'the toll company';
  const amount = fmtMoney(fine.amount);

  const upload = async (file) => {
    if (!file) return;
    setError(''); setResult(null); setBusy(true);
    try {
      const u = await fileToUpload(file);
      const r = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'uploadFineReceipt', fineId: doc.id.replace(/^fine:/, ''), fileBase64: u.base64, contentType: u.contentType, filename: u.filename }),
      }).then((x) => x.json());
      if (!r || !r.success) { setError((r && r.error) || 'Upload failed. Please try again.'); return; }
      setResult(r.pay && r.pay.payment);
      onChanged && onChanged();
    } catch (e) { setError(e.message || 'Upload failed. Please try again.'); }
    finally { setBusy(false); }
  };
  const pick = (e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; upload(f); };

  const uploadButtons = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
      <label style={{ ...btn, background: 'var(--hp-gold)', color: '#1a1a2e', border: 'none', opacity: busy ? 0.6 : 1 }}>
        <input type="file" accept="image/*" capture="environment" onChange={pick} disabled={busy} style={{ display: 'none' }} />
        <Camera size={14} /> Take a photo of the receipt
      </label>
      <label style={{ ...btn, opacity: busy ? 0.6 : 1 }}>
        <input type="file" accept="application/pdf,image/*" onChange={pick} disabled={busy} style={{ display: 'none' }} />
        <UploadCloud size={14} /> Upload PDF or screenshot
      </label>
    </div>
  );

  // ── Paid: the stamp, and nothing left to do ──
  if (paid) {
    return (
      <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 'var(--radius)', background: tone.bg, border: `2px solid ${tone.bd}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 900, fontSize: '1.15rem', letterSpacing: '0.12em', color: tone.fg, border: `2px solid ${tone.fg}`, borderRadius: 6, padding: '2px 10px', transform: 'rotate(-4deg)', display: 'inline-block' }}>PAID</span>
          <div style={{ fontSize: '0.82rem', lineHeight: 1.45 }}>
            <div style={{ fontWeight: 700 }}>{p.paidOn ? `Paid ${fmtDay(p.paidOn)}` : 'Paid'}{p.amount != null ? ` · ${fmtMoney(p.amount)}` : ''}</div>
            {p.receiptNumber && <div style={{ color: 'var(--hp-dim)' }}>Receipt {p.receiptNumber}</div>}
            <div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>Stamped {fmtWhen(p.stampedAt)}</div>
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--hp-text)' }}>Thank you — this notice is finished. Nothing more to do.</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10, fontSize: '0.84rem', color: 'var(--hp-text)', lineHeight: 1.5 }}>
      {/* Headline: what, how much, by when */}
      <div style={{ padding: '12px 14px', borderRadius: 'var(--radius)', background: tone.bg, border: `2px solid ${tone.bd}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <AlertTriangle size={18} color={tone.fg} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.98rem' }}>You need to pay this notice yourself</div>
            <div style={{ fontWeight: 700, marginTop: 2 }}>
              {amount} by {fmtDay(pay.dueDate) || 'the due date'}
              {countdown(pay) && <span style={{ color: tone.fg, marginLeft: 8 }}>· {countdown(pay)}</span>}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: '0.8rem' }}>
          This is a <b>toll payment demand</b> for a trip in your van. Unlike a fine, a toll can't be moved into your name, so
          <b> no other notice will be mailed to you — this is the one to pay.</b>
        </div>
      </div>

      {/* Receipt states */}
      {p && p.status === 'check' && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-surface)' }}>
          <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} color="var(--hp-gold)" /> Receipt received {fmtWhen(p.uploadedAt)} — the office is checking it</div>
          {(p.checks || []).length > 0 && <ul style={{ margin: '6px 0 0 18px', padding: 0, color: 'var(--hp-dim)', fontSize: '0.78rem' }}>{p.checks.map((c, i) => <li key={i}>{c}</li>)}</ul>}
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 6 }}>Uploaded the wrong file? Send the right one below.</div>
        </div>
      )}
      {p && p.status === 'rejected' && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid #dc2626', background: 'rgba(214,69,69,0.08)' }}>
          <div style={{ fontWeight: 700, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}><XCircle size={14} /> Your receipt was not accepted</div>
          <div style={{ fontSize: '0.8rem', marginTop: 3 }}>{p.rejectedReason}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>Please pay the notice and upload the receipt again.</div>
        </div>
      )}

      {/* Steps */}
      <ol style={{ margin: '12px 0 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <li>
          <b>Pay exactly {amount} to {issuer}.</b> Quote the notice number <b style={{ fontFamily: 'var(--font-mono)' }}>{fine.penaltyNumber}</b>
          {fine.rego ? <> and the plate <b style={{ fontFamily: 'var(--font-mono)' }}>{fine.rego}</b></> : null}. Pick any one way:
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8, padding: 10, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
            {h.payUrl && (
              <a href={`https://${h.payUrl}`} target="_blank" rel="noreferrer" style={{ ...btn, background: 'var(--hp-gold)', color: '#1a1a2e', border: 'none', textDecoration: 'none', justifyContent: 'center' }}>
                <Globe size={14} /> Pay online — {h.payUrl}
              </a>
            )}
            {h.payPhone && (
              <a href={telHref(h.payPhone)} style={{ ...btn, textDecoration: 'none', justifyContent: 'center' }}>
                <Phone size={14} /> Pay by phone — {h.payPhone}
              </a>
            )}
            {(h.bpayBiller || h.bpayRef) && (
              <div style={{ borderTop: '1px dashed var(--hp-border)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontWeight: 700, fontSize: '0.8rem' }}>BPAY from your banking app</div>
                <CopyRow label="Biller code" value={h.bpayBiller} />
                <CopyRow label="Reference" value={h.bpayRef} />
              </div>
            )}
            {/linkt/i.test(issuer) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
                <Smartphone size={13} /> Or use the Linkt app (App Store / Google Play).
              </div>
            )}
          </div>
          <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
            Pay the <b>exact</b> amount. Opening a toll account or topping one up does <b>not</b> pay this demand.
          </div>
          {h.notes && <div style={{ marginTop: 4, fontSize: '0.78rem', color: 'var(--hp-dim)', whiteSpace: 'pre-wrap' }}>{h.notes}</div>}
        </li>
        <li>
          <b>Keep the receipt.</b> Screenshot the payment confirmation, or keep the receipt email / PDF.
          A BPAY receipt from your bank is fine.
        </li>
        <li>
          <b>Upload it here.</b> We read the receipt number and mark the notice <b>PAID</b> — you'll see the stamp on this card.
          {uploadButtons}
          {busy && <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-dim)' }}><Loader size={14} /> Reading your receipt…</div>}
          {error && <div style={{ marginTop: 8, color: 'var(--hp-red)', fontSize: '0.8rem' }}>{error}</div>}
          {result && result.status === 'check' && <div style={{ marginTop: 8, fontSize: '0.8rem' }}>Receipt saved. We couldn't match it automatically, so the office will check it.</div>}
        </li>
      </ol>

      <div style={{ marginTop: 10, fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
        Pay before the due date — after that more fees are added and the toll company can take action on the van's registration.
        Can't pay in time or not sure it's yours? Tell the office straight away.
      </div>
    </div>
  );
}

const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 12px', fontSize: '0.82rem', fontWeight: 700, borderRadius: 'var(--radius)', cursor: 'pointer', border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)' };
